import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import Media from 'sb-media'
import cx from 'classnames'
import { constants, number } from 'common/helpers'

import injectGlobals from 'decorators/injectGlobals'

import { Image, LazyImage, UpchargePrice, ImageWithPlaceholderFallback } from 'components/_new/ui'
import Text from 'components/_new/typography/Text/Text'
import { AddToQueueOrCartButton } from 'components/_new/controls'

import Link from 'components/ui/Link/Link'
import Button from 'components/buttons/Button/Button'
import RatingStars from 'components/rating/RatingStars/RatingStars'
import ProductLabels from 'components/product/ProductLabels/ProductLabels'

import messages from './messages'
import s from './ProductCardBase.scss'


class ProductCardBase extends Component {

  static propTypes = {
    children: PropTypes.node,
    containerClassName: PropTypes.string,
    className: PropTypes.string,
    buttonTitle: PropTypes.object,
    link: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
    addTo: PropTypes.oneOf([ 'queue', 'cart', false ]),
    data: PropTypes.object,
    metadata: PropTypes.shape({
      placement: PropTypes.string,
      premium: PropTypes.number,
    }),
    snowplowMetadata: PropTypes.object,
    lazyImage: PropTypes.bool,
    content: PropTypes.node,
    imgSrc: PropTypes.string,
    isHoverEffect: PropTypes.bool,
    isButtonAlways: PropTypes.bool, // set to true if you need desktop style on mobile screens
    onBlockClick: PropTypes.func,
    onButtonClick: PropTypes.func,
    onAfterAdd: PropTypes.func,
    isWholeBlockAsButton: PropTypes.bool,
    isAddToButtonHidden: PropTypes.bool,
    isRatingStarBlack: PropTypes.bool,
  }

  static defaultProps = {
    link: false,
    addTo: false,
    isHoverEffect: false,
    isButtonAlways: false,
    isAddToButtonHidden: false,
    isRatingStarBlack: false,
    data: {},
  }

  handleBlockClick = (event) => {
    const { onBlockClick, link } = this.props

    if (!link) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (typeof onBlockClick === 'function') {
      onBlockClick(event)
    }
  }

  // for qa purposes
  getTestItemType = () => {
    const { data: { labels = [] } } = this.props

    if (labels.includes('limited-edition-sold-out')) {
      return constants.testIds.common.limitedProductOutOfStock
    }

    if (labels.includes('limited-edition')) {
      return constants.testIds.common.limitedProduct
    }

    return constants.testIds.common.product
  }

  renderImg = ({ src, className }) => {
    const { isMobile, lazyImage, data: { brand, name }, isZinaEnabled } = this.props

    const alt = `${brand} ${name}`

    if (isZinaEnabled && isMobile) {
      return (
        <Image
          key={src}
          className={className}
          src={src}
          alt={alt}
          resizeBy="height"
        />
      )
    }

    return (
      lazyImage ? (
        <LazyImage
          key={src}
          className={className}
          src={src}
          h={isMobile ? 150 : 200}
          x2
          x3
          alt={alt}
          withIntersector
        />
      ) : (
        <ImageWithPlaceholderFallback
          key={src}
          className={className}
          src={src}
          h={isMobile ? 150 : 200}
          x2
          x3
          resizeBy="height"
          alt={alt}
        />
      )
    )
  }

  render() {
    const {
      children, containerClassName, className, isRatingStarBlack, imgSrc,
      data: { labels = [], rating, totalPurchases, upchargePrice, media }, data,
      link, content, addTo, buttonTitle, onButtonClick, onAfterAdd,
      isHoverEffect, isButtonAlways, isWholeBlockAsButton, isAddToButtonHidden,
      metadata, snowplowMetadata, _ref,
    } = this.props

    const baseNode = link ? Link : 'div'

    const baseProps = {
      className: cx(s.productBase, className, {
        [s.hoverEffect]: isHoverEffect,
      }),
      to: link || null,
      onClick: this.handleBlockClick,
    }

    const rootClassName = cx(containerClassName, s.productBaseContainer, {
      [s.wholeBlockAsButton]: isWholeBlockAsButton,
      [s.buttonAlways]: isButtonAlways,
    })

    const testItemType = this.getTestItemType()

    const images = []

    if (imgSrc) {
      images.push({ src: imgSrc, className: s.image })
    }

    if (media && media[1]) {
      images.push({ src: media[1].preview, className: s.imageOnHover })
    }

    let totalPurchasesMessage

    if (typeof totalPurchases === 'number') {
      totalPurchasesMessage = {
        ...messages.totalPurchases,
        values: { count: number.numberToCommaSeparatedString(totalPurchases) },
      }
    }

    return (
      <div
        className={rootClassName}
        data-testid="productCard"
        data-item={testItemType}
        ref={_ref}
      >
        {
          React.createElement(baseNode, baseProps,
            Boolean(upchargePrice) && (
              <UpchargePrice className={s.upchargePrice} price={upchargePrice} />
            ),
            <ProductLabels className={s.labels} items={labels} />,
            <div className={s.imageContainer}>
              <div className={s.imageContent}>
                <Fragment>
                  {
                    images.map((image) => this.renderImg(image))
                  }
                </Fragment>
              </div>
            </div>,
            <div className={s.content}>
              {content}
              {children}
            </div>,
            Boolean(rating) && (
              <RatingStars className={s.rating} sm value={rating} isBlack={isRatingStarBlack} />
            ),
            totalPurchasesMessage && (
              <Text
                className={s.totalPurchases}
                message={totalPurchasesMessage}
                size="14-24"
                uppercase
                semibold
              />
            ),
            !isAddToButtonHidden && (
              <div className={s.footer}>
                <Media mobile>
                  {
                    (isMobile) => {
                      const buttonStyle = isMobile ? {
                        blackTextButton: !isButtonAlways,
                        brand: isButtonAlways,
                        uppercase: true,
                        xsm: true,
                      } : {
                        fullWidth: true,
                        brand: true,
                        md: true,
                      }

                      if (addTo) {
                        return (
                          <AddToQueueOrCartButton
                            component={Button}
                            className={s.button}
                            addTo={addTo}
                            product={data}
                            metadata={metadata}
                            snowplowMetadata={snowplowMetadata}
                            title={buttonTitle}
                            dataTestId={`${addTo}AddButton`}
                            onAfterAdd={onAfterAdd}
                            {...buttonStyle}
                          />
                        )
                      }

                      return (
                        <Button
                          className={s.button}
                          dataTestId={`${addTo}AddButton`}
                          title={buttonTitle}
                          onClick={onButtonClick}
                          {...buttonStyle}
                        />
                      )
                    }
                  }
                </Media>
              </div>
            )
          )
        }
      </div>
    )
  }
}


export default injectGlobals(({ globals: { device: { isMobile } }, isZinaEnabled }) => ({
  isZinaEnabled,
  isMobile,
}))(ProductCardBase)
